import { DefaultError, useMutation } from "@tanstack/react-query";
import { useQueryToast } from "hooks";
import i18next from "i18next";
import { useNavigate } from "react-router-dom";
import { apiClient } from "utils";
import { useResponseHandler } from "utils/service/responseHandler";
import { warningToast } from "utils/toast/warningToast";
import {
  IResendTwoFactor,
  LoginResponse,
  LoginTwoFactorMutation,
} from "./types";
import { useStoreState } from "providers/StoreStateContext";

export const useTwoFactor = () => {
  const { tokenData } = useStoreState();
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useTwoFactor");
  console.log("tokenData in useTwoFactor:", tokenData);
  return useMutation<any, DefaultError>({
    async mutationFn() {
      loading(i18next.t("service-message.auth.enable-two-factor.loading"));
      const body = { userId: tokenData?.UserId };
      const response = await apiClient({
        method: "POST",
        url: "Auth/EnableTwoFactor",
        body,
      });

      return responseHandler(response, { groupName: "useTwoFactor" });
    },
    onError: dismiss,
    onSuccess: () =>
      success(i18next.t("service-message.auth.enable-two-factor.success")),
  });
};

export const useDisableTwoFactor = () => {
  const { tokenData } = useStoreState();
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useDisableTwoFactor");
  return useMutation<any, DefaultError>({
    async mutationFn() {
      loading(i18next.t("service-message.auth.disable-two-factor.loading"));
      const body = { userId: tokenData?.UserId };
      const response = await apiClient({
        method: "POST",
        url: "Auth/DisableTwoFactor",
        body,
      });

      return responseHandler(response, { groupName: "useDisableTwoFactor" });
    },
    onError: dismiss,
    onSuccess: () =>
      success(i18next.t("service-message.auth.disable-two-factor.success")),
  });
};

export const useLoginTwoFactor = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useLoginTwoFactor");
  const { setToken, setRefreshToken, setTwoFactorTokenExpiresAt } =
    useStoreState();
  return useMutation<any, DefaultError, LoginTwoFactorMutation>({
    async mutationFn(body) {
      loading(i18next.t("service-message.auth.login-two-factor.loading"));
      const response = await apiClient({
        method: "POST",
        url: "Auth/LoginTwoFactor",
        body,
      });

      return responseHandler(response, { groupName: "useLoginTwoFactor" });
    },
    onError: dismiss,
    onSuccess: (data) => {
      success(i18next.t("service-message.auth.login-two-factor.success"));
      setToken(data.token);
      setRefreshToken(data.refreshToken);
      setTwoFactorTokenExpiresAt(null);
      if (data.isPasswordChangeRequired) {
        warningToast("toast-message.force-change-password");
      }
    },
  });
};

export const useResendTwoFactor = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useResendTwoFactor");
  const navigate = useNavigate();
  const { setTwoFactorTokenExpiresAt } = useStoreState();
  return useMutation<LoginResponse, DefaultError, IResendTwoFactor>({
    async mutationFn(body) {
      loading(i18next.t("service-message.auth.resend-two-factor.loading"));
      const response = await apiClient({
        method: "POST",
        url: "Auth/ResendTwoFactor",
        body,
      });

      return responseHandler(response, { groupName: "useResendTwoFactor" });
    },
    onError: dismiss,
    onSuccess: (data, variables) => {
      const urlWithParams = `/two-factor/${data.token2FA}/${variables.email}`;
      setTwoFactorTokenExpiresAt(data.token2FAExpires);
      navigate(urlWithParams);
      success(i18next.t("service-message.auth.resend-two-factor.success"));
      return;
    },
  });
};

export const useOutsideAuthorization = () => {
  const { tokenData } = useStoreState();
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useTwoFactor");

  return useMutation<any, DefaultError>({
    async mutationFn() {
      loading(i18next.t("service-message.auth.outsideAuthorization.loading"));
      const body = { userId: tokenData?.UserId };
      const response = await apiClient({
        method: "POST",
        url: "Auth/CodeOtpQR",
        body,
      });

      return responseHandler(response, { groupName: "OutsideAuthorization" });
    },
    onError: dismiss,
    onSuccess: () =>
      success(i18next.t("service-message.auth.outsideAuthorization.success")),
  });
};

export const useConfirmTwoFactorOTP = () => {
  const { tokenData } = useStoreState();
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useConfirmTwoFactorOTP");

  return useMutation<any, DefaultError, string>({
    async mutationFn(code: string) {
      loading(i18next.t("service-message.auth.ConfirmTwoFactorOTP.loading"));

      const body = {
        userId: tokenData?.UserId,
        code,
      };

      const response = await apiClient({
        method: "POST",
        url: "Auth/ConfirmTwoFactorOTP",
        body,
      });

      return responseHandler(response, { groupName: "ConfirmTwoFactorOTP" });
    },
    onError: dismiss,
    onSuccess: () =>
      success(i18next.t("service-message.auth.ConfirmTwoFactorOTP.success")),
  });
};
